import React from 'react';
import {} from 'smart-react';
import { getTokensFromStorage } from '../../../Utils/Auth/AuthToken';
import {
  USER_DATA,
  TENANT_ID,
  USER_PRODUCT_FEATURES,
} from '../../../constants/applicationConstants';
import { listProducts } from '../../Products/Services/ProductService';
import { getUserProfile } from '../Profile/Services/ProfileService';
import {
  getPersonalTenant,
  getUserProductFeatures,
} from '../../../Utils/Auth/ValidatePermissions';
import { encryptAndStore } from '../../../Utils/Storage/localStorageUtil';
import {
  encryptAndStoreCookie,
  retrieveAndDecryptCookie,
} from '../../../Utils/Storage/cookieUtil';
import {
  getLookupDataForMultipleTypes,
  setGlobalConfiguration,
} from '../Login/Utils/CommonUtil';
import { getUserInfo } from '../Login/Services/LoginService';
import {
  LOOKUP_TYPES,
  GLOBAL_CONFIG,
} from '../../../constants/applicationConstants';
import { deleteAllCookies } from 'smart-react';
export const AuthContext = React.createContext();

/**
 * Return Auth Context Object
 * @returns
 */
export const useAuth = () => React.useContext(AuthContext);

/**
 * AuthContext Provider to handle UserDetail Data
 * @param {*} param0
 * @returns
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [userAuthObj, setUserAuthObj] = React.useState(null);
  const [authLoader,setAuthLoader] = React.useState(true);
  const [tenantID, setTenantID] = React.useState(
    retrieveAndDecryptCookie(TENANT_ID) ?? '',
  );
  const [featureData, setFeatureData] = React.useState(
    getUserProductFeatures(),
  );
  const [profileData, setProfileData] = React.useState(null);

  /**
   * Set UserDetail Data
   * @param {*} userdt
   */
  const updateUser = (userdt) => {
    setUser({
      ...userdt,
    });
  };

  /**
   * Set UserDetail Tenant
   * @param {*} userdt
   */
  const updateTenant = (tenant) => {
    encryptAndStoreCookie(TENANT_ID, tenant);
    let tempId = retrieveAndDecryptCookie(TENANT_ID);
    setTenantID(tempId);
  };

  // Validate user authentication by checking the user info endpoint.
  // A successful response means the user has a valid authenticated session.
  const checkTokens = async () => {
    try {
      const response = await getUserInfo();
      const userInfo = response?.Payload;
      if (response?.IsSuccess && userInfo) {
        if (!user) {
          updateUser(userInfo);
        }
        encryptAndStore(USER_DATA, userInfo);
      }
      return Boolean(response?.IsSuccess);
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  };

  const clearSiteData = async () => {
    sessionStorage.clear();
    localStorage.clear();
    deleteAllCookies();
  };

  const logoutLocalSession = async ({
    redirectTo = '/login',
    shouldRedirect = true,
  } = {}) => {
    await clearSiteData();
    if (shouldRedirect) {
      window.location.replace(redirectTo);
    }
  };

  // clear session storage upon logout
  const logout = async () => {
    try {
      const rawLogoutUrl = process.env.OIDC_LOGOUT_URL;

      await clearSiteData();

      // Ensure the post-logout redirect returns to /login?logged_out=1 so the
      // login page can avoid showing the "Signing you in / Checking session"
      // loader (which is confusing right after sign-out).
      try {
        if (rawLogoutUrl) {
          const u = new URL(rawLogoutUrl);
          const returnUrl = u.searchParams.get('returnUrl');
          if (returnUrl) {
            // returnUrl might already be absolute; if not, resolve against current origin
            const resolved = returnUrl.startsWith('http')
              ? new URL(returnUrl)
              : new URL(returnUrl, window.location.origin);
            resolved.searchParams.set('logged_out', '1');
            u.searchParams.set('returnUrl', resolved.toString());
          }
          window.location.replace(u.toString());
          return;
        }
      } catch (e) {
        // fallback below
      }

      window.location.replace('/login?logged_out=1');
    } catch (error) {
      console.error('Error during logout:', error);
      await clearSiteData();
      window.location.replace('/login?logged_out=1');
    }
  };

  React.useEffect(() => {
    if (user) {
      getLookupDataForMultipleTypes({
        lookupTypes: LOOKUP_TYPES,
      });
      setGlobalConfiguration({
        configurations: GLOBAL_CONFIG,
      });
    }

    if (!featureData && user) {
      (async () => {
        // First try to get data from localStorage
        const storedProducts = getUserProductFeatures();
        if (storedProducts) {
          // If data exists in localStorage, parse and use it
          setFeatureData(storedProducts);
        } else {
          // If not in localStorage, call the API
          const userData = { ...user, IsPrimary: false };
          if (userData && getPersonalTenant()?.TenantId) {
            const productsData = await listProducts({
              user: userData,
              tenantId: getPersonalTenant()?.TenantId,
              type: 'both',
            });
            setFeatureData(productsData);
            encryptAndStore(`${USER_PRODUCT_FEATURES}`, productsData, false);
          }
        }
      })();
    }
    if (!profileData && user) {
      (async () => {
        const response = await getUserProfile({
          user,
        });
        setProfileData(response?.data);
      })();
    }
  }, [user]);
  return (
    <AuthContext.Provider
      value={{
        setAuthLoader,
        authLoader,
        logout,
        logoutLocalSession,
        checkTokens,
        user,
        userAuthObj,
        updateUser,
        updateTenant,
        tenantID,
        getTokensFromStorage,
        featureData,
        setFeatureData,
        profileData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
